import DOMPurify from 'dompurify'
import React, { FC, useEffect, useState } from 'react'

import { APIENDPOINT, getCWPEnvironmentForURL } from '../../../components/shared/utils/common-utils'
import {
  MicrositeContactProps,
  FetchAdvisorResponse,
  InputValue,
  valueType,
} from './MicrositeContact.types'

const StaticText = {
  toAddress: 'Cwp.Smtpuser4@qa.ampf.com',
  serverAddress: '/api/email',
}

export const MicrositeContact: FC<MicrositeContactProps> = props => {
  const [fetchAdvisorError, setFetchAdvisorError] = useState<boolean>(false)
  const [firstName, setFirstName] = useState<InputValue>({ value: '', error: '' })
  const [lastName, setLastName] = useState<InputValue>({ value: '', error: '' })
  const [leadEmail, setLeadEmail] = useState<InputValue>({ value: '', error: '' })
  const [showModal, setShowModal] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const [formSuccess, setFormSuccess] = useState<boolean>(false)
  const [advisor, setAdvisor] = useState<FetchAdvisorResponse | null>(null)
  const { content } = props
  const {
    content: contactHtml,
    linkText,
    linkType,
    linkTarget,
    linkUrl,
    rightImagePath,
    rightImageAlt,
    bgImage,
    linkDataAnalytics,
  } = content
  const env = getCWPEnvironmentForURL(window.location.href)
  const urlParams = new URLSearchParams(window.location.search)
  const email = urlParams.get('email')
  const testParam = urlParams.get('test') || ''
  let subdomain = ''
  let testEmail = testParam || ''
  const cwpEnv = getCWPEnvironmentForURL(window.location)
  if (cwpEnv.includes('localhost')) {
    subdomain = 'https://www.ameriprise.com'
  } else {
    subdomain = ''
  }
  let toEmailAddress: string | null
  let EMAIL_API_URL: string
  if (cwpEnv.includes('prod')) {
    toEmailAddress = testEmail !== '' ? testEmail : email
    EMAIL_API_URL =
      testEmail !== ''
        ? `https://www.ameriprise.com${StaticText.serverAddress}`
        : StaticText.serverAddress
  } else if (cwpEnv.includes('localhost')) {
    toEmailAddress = testEmail
    EMAIL_API_URL = `https://www.ameriprise.com${StaticText.serverAddress}`
  } else if (cwpEnv?.includes('qa')) {
    toEmailAddress = testEmail !== '' ? testEmail : StaticText.toAddress
    EMAIL_API_URL =
      testEmail !== ''
        ? `https://www.ameriprise.com${StaticText.serverAddress}`
        : StaticText.serverAddress
  } else {
    toEmailAddress = testEmail !== '' ? testEmail : StaticText.toAddress
    EMAIL_API_URL =
      testEmail !== ''
        ? `https://www.ameriprise.com${StaticText.serverAddress}`
        : StaticText.serverAddress
  }

  useEffect(() => {
    if (email) {
      function fetchAdvisor() {
        let ADVISOR_API_URL = `${subdomain}/api/advisorsv22/json/GetAdvisorById?advisorId=${email}`

        fetch(ADVISOR_API_URL, {
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'OData-Version': '4.0',
          },
        })
          .then(res => res.json())
          .then(
            res => {
              if (res?.result?.displayname) {
                setAdvisor(res)
              } else {
                setFetchAdvisorError(true)
              }
            },
            error => {
              setFetchAdvisorError(true)
            }
          )
      }
      fetchAdvisor()
    }
  }, [])

  const validateForm = () => {
    let formValid: boolean = true
    const emailRegex = /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/

    if (!firstName.value.length) {
      setFirstName(state => {
        return { ...state, error: 'Please enter a first name' }
      })
      formValid = false
    }
    if (!lastName.value.length) {
      setLastName(state => {
        return { ...state, error: 'Please enter a last name' }
      })
      formValid = false
    }
    if (!leadEmail.value.length) {
      setLeadEmail(state => {
        return { ...state, error: 'Please enter a valid email' }
      })
      formValid = false
    }
    if (leadEmail.value.length) {
      if (!leadEmail.value.match(emailRegex)) {
        setLeadEmail(state => {
          return { ...state, error: 'Please enter a valid email' }
        })
        formValid = false
      }
    }
    return formValid
  }

  const handleRequestSend = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()

    if (validateForm()) {
      const payload = {
        type: 'email',
        data: {
          from: 'No-reply@ampf.com',
          to: [toEmailAddress],
          subject: 'Inquiry from Lending Tool form on ameriprise.com',
          body: `A potential lead is reaching out to you - ${firstName?.value} ${lastName?.value} | ${leadEmail?.value}<br />URL: <a href=${window.location.href}>${window.location.href}/a><br /><br /><br /><br />For advisor use only. Not for inspection by, distribution or quotation to the general public Ameriprise Financial Services, LLC. Member FINRA and SIPC<br /><br />Copyright Ameriprise Financial Inc. All rights reserved.<br /><br />`,
        },
      }

      try {
        const resp = await fetch(EMAIL_API_URL, {
          method: 'POST',
          mode: 'cors', // no-cors, cors, *same-origin
          cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
          body: JSON.stringify(payload),
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
          },
        })
        const response = await resp.json()

        if (response?.code !== '200') {
          throw new Error('')
        }
        setFormSuccess(true)
      } catch (e) {
        setFormError('Something went wrong. Please try again.')
      }
    }
  }
  const handleCancel = () => {
    const defaultValues: InputValue = {
      value: '',
      error: '',
    }
    setShowModal(false)
    setFirstName(defaultValues)
    setLastName(defaultValues)
    setLeadEmail(defaultValues)
    setFormError('')
    setFormSuccess(false)
  }
  useEffect(() => {
    const handleEsc = (event: any) => {
      if (event.key === 'Escape') {
        handleCancel()
      }
    }
    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])
  const renderRightImage = () => {
    return (
      <div className='u-size1of2 u-md-sizeFull u-sm-sizeFull'>
        <img
          src={APIENDPOINT(env).baseGallery + rightImagePath?.value}
          alt={rightImageAlt?.value || ''}
          className='u-screenHeight u-sizeFull'
        />
      </div>
    )
  }
  const sanitizeUrl = (url: string): string => {
    const SAFE_URL_PATTERN = /^(?!javascript:)(?:[a-z0-9+.-]+:|[^&:\/?#]*(?:[\/?#]|$))/i
    const SAFE_URL_PATTERN2 = /^(?:(?:https?|mailto|ftp|tel|file|sms):|[^&:/?#]*(?:[/?#]|$))/gi
    url = String(url)
    if (url.match(SAFE_URL_PATTERN) && url.match(SAFE_URL_PATTERN2)) return url
    return 'unsafe:' + url
  }
  const getValue = (data: valueType | valueType[], index: number) => {
    return Array.isArray(data) ? data[index]?.value : data?.value
  }
  return (
    <div
      className='u-heightFull u-sm-paddingVert10gu u-md-paddingVert10gu'
      style={{
        backgroundImage: bgImage?.value
          ? `url(${APIENDPOINT(env).baseGallery + bgImage?.value})`
          : 'none',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
      {!email || fetchAdvisorError ? (
        <div className='u-heightFull u-sizeConstrainedLeft u-flex u-flexWrapReverse u-md-colReverse  u-sm-paddingHoriz4gu u-md-paddingHoriz4gu'>
          <div className='u-size1of2 u-paddingLeft3gu u-sm-padding0 u-md-padding0 u-md-sizeFull u-sm-sizeFull u-paddingTop12gu u-paddingRight12gu u-sm-paddingRight0 u-md-paddingRight0'>
            <div
              className='u-marginBottom1gu'
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(contactHtml[0]?.value),
              }}></div>
            <div
              className='u-marginBottom1gu'
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(contactHtml[1]?.value),
              }}></div>
            <div className='u-paddingTop2gu'>
              <a
                className={
                  'u-sizeFull Button u-flexJustifyCenter ' +
                  `${
                    linkType?.value?.includes('primary') ? 'Button--primary' : 'Button--secondary'
                  }`
                }
                href={linkUrl?.value}
                target={linkTarget?.value}
                data-tracking='primary'
                data-analytics={getValue(linkDataAnalytics, 0)}>
                {getValue(linkText, 0)}
              </a>
            </div>
            <div
              className='u-marginTop5gu'
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(contactHtml[2]?.value),
              }}></div>
            <div className='u-paddingTop5gu'>
              <p className='u-textCenter'>{getValue(linkText, 1)}</p>
            </div>
          </div>
          {renderRightImage()}
        </div>
      ) : (
        <>
          {advisor?.result?.displayname && (
            <>
              {showModal && (
                <div className='Modal'>
                  <dialog className='Modal-window'>
                    <div className='Modal-content Content'>
                      {!formSuccess ? (
                        <div>
                          <p className='u-textColorAlert'>{formError}</p>
                          <h2>Request Contact</h2>
                          <p className='u-textColorLight'>
                            Please provide some basic information and an email address where you
                            prefer to be reached.
                          </p>
                          <div className='u-flex u-paddingTop3gu'>
                            <div className='u-marginRight3gu u-size3of6'>
                              <div className='Form-group'>
                                <label
                                  htmlFor={'first-name-input'}
                                  className='Form-label u-textColorLight'>
                                  First Name*
                                </label>
                                <input
                                  onChange={e => setFirstName({ value: e.target.value, error: '' })}
                                  type='text'
                                  value={firstName.value}
                                  id='first-name-input'
                                  className={'Input' + `${firstName.error ? ' Input--error' : ''}`}
                                  name='first-name'
                                  autoFocus
                                />
                                <span className='u-textColorAlert u-textSizeMsrm2'>
                                  {firstName.error}
                                </span>
                              </div>
                            </div>
                            <div className='u-sizeFill'>
                              <div className='Form-group'>
                                <label
                                  htmlFor={'last-name-input'}
                                  className='Form-label u-textColorLight'>
                                  Last Name*
                                </label>
                                <input
                                  onChange={e => setLastName({ value: e.target.value, error: '' })}
                                  type='text'
                                  value={lastName.value}
                                  id='last-name-input'
                                  className={'Input' + `${lastName.error ? ' Input--error' : ''}`}
                                  name='last-name'
                                />
                                <span className='u-textColorAlert u-textSizeMsrm2'>
                                  {lastName.error}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className='Form-group'>
                              <label
                                htmlFor={'lead-email-input'}
                                className='Form-label u-textColorLight'>
                                Email*
                              </label>
                              <input
                                onChange={e => setLeadEmail({ value: e.target.value, error: '' })}
                                type='text'
                                value={leadEmail.value}
                                id='lead-email-input'
                                className={'Input' + `${leadEmail.error ? ' Input--error' : ''}`}
                                name='lead-email'
                              />
                              <span className='u-textColorAlert u-textSizeMsrm2'>
                                {leadEmail.error}
                              </span>
                            </div>
                          </div>
                          <div className='u-flex'>
                            <div className='u-marginRight3gu'>
                              <button
                                onClick={handleRequestSend}
                                className='Button Button--primary u-flex'>
                                Send
                              </button>
                            </div>
                            <div>
                              <button onClick={handleCancel} className='Button Button--secondary'>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className='u-flex u-flexJustifyCenter'>
                            {' '}
                            <img
                              className='u-borderRadious5gu'
                              style={{ borderRadius: '170px' }}
                              width={'170px'}
                              src={advisor?.result?.profilelargeimage}
                              alt=''
                            />
                          </div>
                          <div className='u-flex u-flexJustifyCenter u-marginTop4gu u-marginBottom3gu'>
                            <h2>Thank you!</h2>
                          </div>
                          <div className='u-flex u-flexJustifyCenter'>
                            <p>
                              {advisor?.result?.displayname} will be following up with you soon.
                            </p>
                          </div>
                          <div className='u-flex u-flexJustifyCenter u-marginTop6gu'>
                            <button onClick={handleCancel} className='Button Button--secondary'>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </dialog>
                </div>
              )}

              <div className='u-sizeConstrainedLeft u-flex u-flexWrapReverse u-md-colReverse u-sm-paddingHoriz4gu u-md-paddingHoriz4gu'>
                <div className='u-size1of2 u-paddingLeft3gu u-sm-padding0 u-md-padding0 u-md-sizeFull u-sm-sizeFull u-paddingTop12gu u-paddingRight12gu u-sm-paddingRight0 u-md-paddingRight0'>
                  <div
                    className='u-marginBottom1gu'
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(contactHtml[3]?.value),
                    }}></div>

                  <div
                    className='u-paddingTop3gu u-textColorLight'
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(contactHtml[4]?.value),
                    }}></div>

                  <div className='u-flex u-paddingTop6gu'>
                    <div>
                      <img
                        className='u-borderRadious5gu'
                        style={{ borderRadius: '100px' }}
                        width={'100px'}
                        src={advisor?.result?.profilelargeimage}
                        alt=''
                      />
                    </div>
                    <div className='u-paddingLeft4gu'>
                      <h3>
                        <a target='_blank' href={advisor?.result.contactUrl}>
                          {advisor?.result?.displayname}
                        </a>
                      </h3>
                      <div
                        className='u-paddingTop1gu u-textSecondary u-textItalic'
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(advisor?.result?.professionaldesignations),
                        }}></div>
                      <p className='u-paddingTop1gu'>{advisor?.result?.proffessionaltitle}</p>
                      <p className='u-paddingTop1gu'>
                        {advisor?.result?.phonenumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1.$2.$3')}
                      </p>
                      <p>
                        <a href={sanitizeUrl(`mailto:${email}`)} className='u-paddingTop1gu'>
                          {advisor?.result?.email}
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className='u-paddingTop7gu'>
                    <button
                      onClick={() => setShowModal(true)}
                      className='Button Button--primary u-sizeFull'>
                      Request contact
                    </button>
                  </div>
                  <div className='u-paddingTop5gu'>
                    <p className='u-textCenter'>{getValue(linkText, 1)}</p>
                  </div>
                </div>

                {renderRightImage()}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
